import { ExternalLink, BookOpen } from "lucide-react";

export interface AuthoritySource {
  label: string;
  url: string;
  publisher?: string;
}

interface AuthoritySourcesProps {
  sources: AuthoritySource[];
  heading?: string;
  intro?: string;
}

/**
 * Outbound citation list — boosts E-E-A-T and gives answer engines a
 * grounding trail back to primary sources (IRS.gov, Treasury, Cornell LII).
 * Links carry rel="noopener external nofollow" because they are reference
 * citations rather than editorial endorsements.
 */
export const AuthoritySources = ({
  sources,
  heading = "Primary sources & authority",
  intro = "We cite the underlying IRS publications and statutes so you can verify everything on this page.",
}: AuthoritySourcesProps) => {
  if (!sources.length) return null;
  return (
    <section className="mt-12 border-t border-border pt-8 not-prose">
      <div className="flex items-center gap-2 mb-2">
        <BookOpen className="w-4 h-4 text-gold" />
        <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold">
          {heading}
        </h2>
      </div>
      <p className="text-sm text-muted-foreground mb-4 leading-relaxed">{intro}</p>
      <ul className="space-y-2">
        {sources.map((s) => (
          <li key={s.url}>
            <a
              href={s.url}
              target="_blank"
              rel="noopener external nofollow"
              className="inline-flex items-start gap-2 text-sm text-foreground hover:text-gold transition-colors group"
            >
              <ExternalLink className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground group-hover:text-gold" />
              <span>
                <span className="font-semibold underline underline-offset-2 decoration-muted-foreground/40 group-hover:decoration-gold">
                  {s.label}
                </span>
                {s.publisher && (
                  <span className="text-muted-foreground ml-1.5 text-xs">
                    — {s.publisher}
                  </span>
                )}
              </span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
};
